import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "@/lib/router-compat";
import { getSupabase } from "@/lib/supabase-lazy";
import { useAuthState } from "@/hooks/useAuthState";
import AuthModal from "@/components/LazyAuthModal";
import MarkerHighlight from "@/components/MarkerHighlight";

import { Menu, ChevronDown } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLanguage } from "@/i18n/LanguageContext";
import { useLanguageSwitch } from "@/hooks/useLanguageSwitch";
import { getPanelUrl } from "@/lib/panel-url";
import { useInPublicShell } from "@/components/public/PublicShell";

const Header = () => {
  const inPublicShell = useInPublicShell();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang, t } = useLanguage();
  // Estado por defecto = visitante: los CTA de registro/inicio salen ya en el
  // HTML servido y se sustituyen por "Ir al Panel" cuando hay sesión.
  const { isAuthenticated: isLoggedIn } = useAuthState();

  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState<"login" | "signup">("login");
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (isLoggedIn && authOpen) {
      setAuthOpen(false);
    }
  }, [isLoggedIn, authOpen]);

  const handlePanelClick = () => {
    setMobileOpen(false);
    // El panel abre siempre en la Hoja de Ruta.
    const url = getPanelUrl("/dashboard?tab=roadmap");
    if (url.startsWith("http")) {
      window.location.href = url;
    } else {
      navigate(url);
    }
  };

  const _handleNavClick = (link: { href: string; isRoute?: boolean }) => {
    setMobileOpen(false);
    if (link.isRoute) {
      navigate(link.href);
    } else {
      window.location.href = link.href;
    }
  };

  const handleLogout = async () => {
    setMobileOpen(false);
    const supabase = await getSupabase();
    await supabase.auth.signOut();
    navigate("/");
  };

const navLinks: { label: string; href: string; isRoute?: boolean }[] = [
  { label: t("header", "methodology"), href: "#sectores" },
  { label: t("header", "thePanel"), href: "#estrategia" },
  { label: t("header", "agent"), href: "#agente-ia" },
  { label: lang === "es" ? "Tarifas" : "Pricing", href: lang === "es" ? "/tarifas" : "/en/pricing", isRoute: true },
];

  // Language switcher physically navigates between localized URLs
  // (SEO international routing). Shared with the panel sidebar so both
  // selectors behave exactly the same.
  const { toggleLang } = useLanguageSwitch();


  // Dentro de la nueva envoltura pública la navegación vive en el menú
  // lateral: este menú superior antiguo no debe duplicarse.
  if (inPublicShell) return null;

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-card/80 backdrop-blur-md shadow-xs border-b border-border">
        <div className="container flex items-center justify-between h-16">
          <a href="/" className="font-display text-2xl font-bold tracking-tight flex items-center">
            RETAIL<MarkerHighlight height="0.78em" className="px-[0.06em] text-foreground">LEMON</MarkerHighlight>
            <span className="ml-2 text-[10px] font-bold uppercase tracking-wider bg-[#FFE143]/30 text-[#5C5100] px-2 py-0.5 rounded-full">Beta</span>
          </a>

          {/* Desktop nav (≥1024px) */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href + link.label}
                href={link.href}
                onClick={(e) => {
                  e.preventDefault();
                  if (link.isRoute) {
                    navigate(link.href);
                    window.scrollTo(0, 0);
                  } else if (location.pathname !== "/") {
                    navigate("/" + link.href);
                  } else {
                    const id = link.href.replace("#", "");
                    const el = document.getElementById(id);
                    if (el) el.scrollIntoView({ behavior: "smooth" });
                  }
                }}
                className="font-body text-sm font-medium hover:text-foreground transition-colors"
              >
                {link.label}
              </a>
            ))}
            <button
              onClick={toggleLang}
              className="font-body text-xs font-semibold border border-border px-2.5 py-1 rounded-full hover:bg-secondary transition-colors"
              aria-label={lang === "es" ? "Cambiar el idioma del sitio a inglés (menú principal)" : "Switch site language to Spanish (main menu)"}
            >
              {lang === "es" ? "EN" : "ES"}
            </button>
            {isLoggedIn ? (

              <div className="flex items-center gap-3">
                <button
                  onClick={handlePanelClick}
                  className="font-body text-sm font-semibold bg-[#FFE143] text-slate-900 px-5 py-2 rounded-full hover:bg-[#FFE143]/90 shadow-md transition-all"
                >
                  {lang === "es" ? "Ir al Panel" : "Go to Panel"}
                </button>
                <button
                  onClick={handleLogout}
                  className="font-body text-sm text-muted-foreground hover:text-destructive transition-colors"
                >
                  {t("header", "logout")}
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <button
                  onClick={() => { setAuthMode("signup"); setAuthOpen(true); }}
                  className="font-body text-sm font-semibold bg-[#FFE143] text-black px-4 py-2 rounded-full hover:bg-[#FFE143]/90 shadow-md transition-all"
                >
                  {lang === "es" ? "Regístrate" : "Sign up"}
                </button>
                <button
                  onClick={() => { setAuthMode("login"); setAuthOpen(true); }}
                  className="font-body text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
                >
                  {lang === "es" ? "Iniciar sesión" : "Sign in"}
                </button>
              </div>

            )}
          </nav>

          {/* Tablet nav (768-1023px): dropdown for nav + inline actions */}
          <div className="hidden md:flex lg:hidden items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger className="inline-flex items-center gap-1 font-body text-sm font-medium px-3 py-1.5 rounded-full border border-border hover:bg-secondary transition-colors">
                {lang === "es" ? "Menú" : "Menu"}
                <ChevronDown className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {navLinks.map((link) => (
                  <DropdownMenuItem
                    key={link.href + link.label}
                    onSelect={(e) => {
                      e.preventDefault();
                      if (link.isRoute) {
                        navigate(link.href);
                        window.scrollTo(0, 0);
                      } else if (location.pathname !== "/") {
                        navigate("/" + link.href);
                      } else {
                        const id = link.href.replace("#", "");
                        const el = document.getElementById(id);
                        if (el) el.scrollIntoView({ behavior: "smooth" });
                      }
                    }}
                    className="cursor-pointer font-body text-sm"
                  >
                    {link.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <button
              onClick={toggleLang}
              className="font-body text-xs font-semibold border border-border px-2.5 py-1 rounded-full hover:bg-secondary transition-colors"
              aria-label={lang === "es" ? "Cambiar el idioma del sitio a inglés (menú compacto)" : "Switch site language to Spanish (compact menu)"}
            >
              {lang === "es" ? "EN" : "ES"}
            </button>
            {isLoggedIn ? (

              <div className="flex items-center gap-2">
                <button
                  onClick={handlePanelClick}
                  className="font-body text-sm font-semibold bg-[#FFE143] text-slate-900 px-4 py-2 rounded-full hover:bg-[#FFE143]/90 shadow-md transition-all"
                >
                  {lang === "es" ? "Ir al Panel" : "Go to Panel"}
                </button>
                <button
                  onClick={handleLogout}
                  className="font-body text-xs text-muted-foreground hover:text-destructive transition-colors whitespace-nowrap"
                >
                  {t("header", "logout")}
                </button>
              </div>
            ) : (
              <>
                <button
                  onClick={() => { setAuthMode("signup"); setAuthOpen(true); }}
                  className="font-body text-sm font-semibold bg-[#FFE143] text-black px-4 py-2 rounded-full hover:bg-[#FFE143]/90 shadow-md transition-all whitespace-nowrap"
                >
                  {lang === "es" ? "Regístrate" : "Sign up"}
                </button>
                <button
                  onClick={() => { setAuthMode("login"); setAuthOpen(true); }}
                  className="font-body text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors whitespace-nowrap"
                >
                  {lang === "es" ? "Iniciar sesión" : "Sign in"}
                </button>
              </>

            )}
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(true)}
            className="md:hidden p-2 rounded-lg hover:bg-accent/20 transition-colors"
            aria-label={lang === "es" ? "Abrir el menú de navegación" : "Open navigation menu"}
          >
            <Menu className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
      </header>

      {/* Mobile sheet */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="right" className="w-72">
          <SheetHeader>
            <SheetTitle className="font-display text-xl font-bold tracking-tight text-left flex items-center">
              RETAIL<MarkerHighlight height="0.78em" className="px-[0.06em] text-foreground">LEMON</MarkerHighlight>
              <span className="ml-2 text-[10px] font-bold uppercase tracking-wider bg-[#FFE143]/30 text-[#5C5100] px-2 py-0.5 rounded-full">Beta</span>
            </SheetTitle>
          </SheetHeader>
          <nav className="flex flex-col gap-2 mt-6">
{navLinks.map((link) => (
              <a
                key={link.href + link.label}
                href={link.href}
                onClick={(e) => {
                  setMobileOpen(false);
                  e.preventDefault();
                  if (link.isRoute) {
                    navigate(link.href);
                    window.scrollTo(0, 0);
                  } else if (location.pathname !== "/") {
                    navigate("/" + link.href);
                  } else {
                    const id = link.href.replace("#", "");
                    const el = document.getElementById(id);
                    if (el) el.scrollIntoView({ behavior: "smooth" });
                  }
                }}
                className="font-body text-base font-medium px-3 py-2.5 rounded-lg hover:bg-accent/20 transition-colors"
              >
                {link.label}
              </a>
            ))}
            <div className="border-t border-border my-2" />
            <div className="flex gap-2 px-3">
              <button
                onClick={() => { if (lang !== "es") toggleLang(); }}
                aria-label={lang === "es" ? "Idioma del sitio: español (activo)" : "Cambiar el idioma del sitio a español"}
                aria-pressed={lang === "es"}
                className={`font-body text-xs font-semibold border px-3 py-1.5 rounded-full transition-colors ${lang === "es" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}
              >
                ES
              </button>
              <button
                onClick={() => { if (lang !== "en") toggleLang(); }}
                aria-label={lang === "en" ? "Site language: English (active)" : "Switch site language to English"}
                aria-pressed={lang === "en"}
                className={`font-body text-xs font-semibold border px-3 py-1.5 rounded-full transition-colors ${lang === "en" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}
              >
                EN
              </button>
            </div>
            <div className="border-t border-border my-2" />
            {isLoggedIn ? (
              <div className="flex flex-col gap-3 px-3">
                <button
                  onClick={handlePanelClick}
                  className="w-full font-body text-sm font-semibold bg-[#FFE143] text-slate-900 px-5 py-2.5 rounded-full hover:bg-[#FFE143]/90 shadow-md transition-all"
                >
                  {lang === "es" ? "Ir al Panel" : "Go to Panel"}
                </button>
                <button
                  onClick={handleLogout}
                  className="font-body text-sm text-muted-foreground hover:text-destructive transition-colors text-center py-1"
                >
                  {t("header", "logout")}
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center px-3">
                <button
                  onClick={() => { setMobileOpen(false); setAuthMode("signup"); setAuthOpen(true); }}
                  className="w-full font-body text-sm font-semibold bg-[#FFE143] text-slate-900 px-5 py-2.5 rounded-full hover:bg-[#FFE143]/90 shadow-md transition-all"
                >
                  {lang === "es" ? "Regístrate" : "Sign up"}
                </button>
                <button
                  onClick={() => { setMobileOpen(false); setAuthMode("login"); setAuthOpen(true); }}
                  className="mt-3 text-sm text-center text-slate-500 hover:text-slate-700 hover:underline transition-colors"
                >
                  {lang === "es" ? "Iniciar sesión" : "Sign in"}
                </button>
              </div>

            )}
          </nav>
        </SheetContent>
      </Sheet>

      <AuthModal open={authOpen} onOpenChange={setAuthOpen} initialMode={authMode} />
    </>
  );
};

export default Header;
